/**
 * MKS-183 — collapsible blocks (`+++ Title … +++`) in descriptions, comments & AC.
 *
 * Linear's syntax, already present in data imported from Linear and emitted by
 * the TidyMonk `/dev` and `/test` report skills:
 *
 * ```
 * +++ Gate log
 * …any markdown…
 * +++
 * ```
 *
 * renders as a collapsed `<details>` with `Gate log` as its `<summary>`.
 *
 * Why not `<details>` written as raw HTML: honouring that would mean enabling
 * `rehype-raw`, i.e. parsing raw HTML in every user-authored body. That is a
 * much bigger change to the security model than one remark plugin, so raw HTML
 * stays off and raw `<details>` keeps being dropped.
 *
 * The sanitization schema needs no change either: `hast-util-sanitize`'s
 * `defaultSchema` already allows the `details` and `summary` tags.
 *
 * ## Why this works on the source and not on the parsed tree
 *
 * A `+++` block is invisible in the mdast tree. Without blank lines remark folds
 * the opener, the body and the closer into a single paragraph joined by soft
 * breaks, so a tree-level transform would see one text blob and would have to
 * give up on tables, fenced code and lists inside the block. Instead the plugin
 * scans the source line by line and re-parses each segment with the processor's
 * own `parse()` — which keeps every configured parser extension (remark-gfm)
 * active inside blocks. `remarkTaskRefs` runs afterwards as a transformer over
 * the whole tree, so `#CODE` chips and mentions inside a block work unchanged.
 */

/** Minimal structural mdast typing — matches the style of `taskRefEncoding`. */
interface MdNode {
  type: string;
  value?: string;
  data?: { hName?: string };
  children?: MdNode[];
}

interface MdRoot extends MdNode {
  type: 'root';
  children: MdNode[];
}

/**
 * The slice of the unified processor we need. `parse` is declared as returning
 * `unknown` so the attacher stays assignable to unified's `Plugin` type, whose
 * `this` is a `Processor` returning a bare `Node`; `parseChildren` does the one
 * narrowing cast.
 */
interface ParserHost {
  parse(doc: string): unknown;
}

/** Parse a markdown segment and hand back its root-level children. */
function parseChildren(host: ParserHost, doc: string): MdNode[] {
  return (host.parse(doc) as MdRoot).children ?? [];
}

/**
 * Does `delim` close an open fence of `fence`? Same character, and at least as
 * long (CommonMark).
 */
function closesFence(delim: string | undefined, fence: string): boolean {
  return delim !== undefined && delim.charAt(0) === fence.charAt(0) && delim.length >= fence.length;
}

/**
 * Opener: up to three leading spaces (four would be an indented code block),
 * `+++`, then a non-empty title. The title is required — a bare `+++` outside a
 * block is never an opener, so a stray `+++` can never swallow the rest of the
 * body.
 */
const OPEN_RE = /^ {0,3}\+\+\+[ \t]+(\S.*?)[ \t]*$/;

/** Closer: a bare `+++` on its own line. */
const CLOSE_RE = /^ {0,3}\+\+\+[ \t]*$/;

/** Fence delimiter (``` or ~~~), captured so only a matching run closes it. */
const FENCE_RE = /^ {0,3}(`{3,}|~{3,})/;

/**
 * Walk `lines[from..to)` tracking fenced-code state, and report the index of the
 * `+++` that closes the block opened just before `from`. Nested openers push
 * depth, so an inner block consumes its own closer. Returns -1 when the block is
 * never closed.
 */
function findCloser(lines: string[], from: number, to: number): number {
  let depth = 0;
  let fence: string | null = null;
  for (let i = from; i < to; i++) {
    const line = lines[i] ?? '';
    const delim = FENCE_RE.exec(line)?.[1];
    if (fence) {
      // Inside a fence everything — `+++` included — is content.
      if (closesFence(delim, fence)) fence = null;
      continue;
    }
    if (delim) {
      fence = delim;
      continue;
    }
    if (CLOSE_RE.test(line)) {
      if (depth === 0) return i;
      depth--;
      continue;
    }
    if (OPEN_RE.test(line)) depth++;
  }
  return -1;
}

/**
 * Turn `lines[from..to)` into mdast children, replacing every closed `+++`
 * block with a `details` node. Plain stretches are re-parsed as a unit so
 * multi-line constructs (tables, fences, lists) survive.
 */
function buildChildren(host: ParserHost, lines: string[], from: number, to: number): MdNode[] {
  const out: MdNode[] = [];
  let pending: string[] = [];

  const flush = (): void => {
    if (pending.length === 0) return;
    out.push(...parseChildren(host, pending.join('\n')));
    pending = [];
  };

  let fence: string | null = null;
  for (let i = from; i < to; i++) {
    const line = lines[i] ?? '';
    const delim = FENCE_RE.exec(line)?.[1];
    if (fence) {
      if (closesFence(delim, fence)) fence = null;
      pending.push(line);
      continue;
    }
    if (delim) {
      fence = delim;
      pending.push(line);
      continue;
    }

    const open = OPEN_RE.exec(line);
    if (open) {
      const closer = findCloser(lines, i + 1, to);
      if (closer !== -1) {
        flush();
        out.push({
          type: 'collapsible',
          data: { hName: 'details' },
          children: [
            {
              type: 'collapsibleSummary',
              data: { hName: 'summary' },
              children: summaryChildren(host, open[1] ?? ''),
            },
            ...buildChildren(host, lines, i + 1, closer),
          ],
        });
        i = closer;
        continue;
      }
      // Unclosed opener — stays literal text, swallows nothing.
    }
    pending.push(line);
  }

  flush();
  return out;
}

/**
 * Parse the summary title as inline markdown so `+++ **Gate** log` keeps its
 * emphasis. Anything that does not parse to a single paragraph (a title that
 * looks like a list marker, say) falls back to literal text.
 */
function summaryChildren(host: ParserHost, title: string): MdNode[] {
  const parsed = parseChildren(host, title);
  const first = parsed[0];
  if (parsed.length === 1 && first?.type === 'paragraph' && first.children) {
    return first.children;
  }
  return [{ type: 'text', value: title }];
}

/**
 * Remark plugin. Registered BEFORE `remarkTaskRefs` so task-ref and mention
 * linkification still sees the re-parsed block contents.
 */
export function remarkCollapsible(this: ParserHost) {
  // Arrow so the transform keeps the attacher's `this` — the frozen processor,
  // whose `parse` carries every configured parser extension (remark-gfm).
  return (tree: MdRoot, file: { value?: unknown }): void => {
    const source = typeof file.value === 'string' ? file.value : '';
    // Hot path: the overwhelming majority of bodies contain no `+++` at all.
    if (!source.includes('+++')) return;

    const lines = source.split('\n');
    const rebuilt = buildChildren(this, lines, 0, lines.length);
    // Only replace the tree when a block was actually found; otherwise keep the
    // original parse (identical content, but with correct source positions).
    // Blocks are always emitted at the top level of `rebuilt` — a nested one
    // lives inside its parent `collapsible` — so a shallow check is enough.
    if (rebuilt.some((node) => node.type === 'collapsible')) tree.children = rebuilt;
  };
}
